import { useCallback, useState, useRef, useEffect } from 'react'
import { useAuth } from './useAuth'
import { fetchWithAuth, AuthExpiredError } from '@/lib/fetchWithAuth'
import { useDashboardStore } from '@/store/dashboardStore'
import {
  parseBlockEvent,
  parseTitleEvent,
  parseSummaryEvent,
  parseErrorEvent,
  parseFinalEvent,
  type ConnectionStatus,
} from '@/types/sse'
import { parseNodeStartEvent, parseNodeCompleteEvent } from '@/types/orchestrator'

interface UseBlockStreamOptions {
  agentId: string
  baseUrl?: string
  sseBaseUrl?: string // Direct backend URL for SSE (bypasses Vite proxy)
}

interface UseBlockStreamReturn {
  status: ConnectionStatus
  startStream: (query: string, context?: Record<string, unknown>) => void
  stopStream: () => void
  error: Error | null
  isStreaming: boolean
}

/**
 * Higher-level hook for streaming UI blocks from an agent
 * Uses POST-based SSE with fetch + ReadableStream to support large payloads
 * that exceed URL length limits (e.g., file content > 2KB)
 */
export function useBlockStream(options: UseBlockStreamOptions): UseBlockStreamReturn {
  // sseBaseUrl bypasses Vite proxy for SSE - direct connection to backend avoids proxy timeout issues
  const { agentId, sseBaseUrl = import.meta.env.VITE_SSE_BASE_URL || '/api' } = options

  const [status, setStatus] = useState<ConnectionStatus>('disconnected')
  const [error, setError] = useState<Error | null>(null)
  const [isStreaming, setIsStreaming] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)
  const streamStartRef = useRef<number>(0)

  const {
    setTitle,
    setSummary,
    addBlock,
    setError: setStoreError,
    setStatus: setStoreStatus,
    setTraceId,
    setDuration,
    clearBlocks,
    resetOrchestratorState,
    addNodeExecution,
    updateNodeExecution,
  } = useDashboardStore()

  /**
   * Handle SSE events by type and update the dashboard store
   */
  const handleSSEEvent = useCallback(
    (eventType: string, data: unknown) => {
      switch (eventType) {
        case 'title': {
          const parsed = parseTitleEvent(data)
          if (parsed) setTitle(parsed.title)
          break
        }
        case 'summary': {
          const parsed = parseSummaryEvent(data)
          if (parsed) setSummary(parsed.summary)
          break
        }
        case 'node_start': {
          const parsed = parseNodeStartEvent(data)
          if (parsed) {
            addNodeExecution({
              nodeId: parsed.node_id,
              nodeName: parsed.node_name,
              phase: parsed.phase,
              status: 'running',
              startedAt: Date.now(),
            })
          }
          break
        }
        case 'node_complete': {
          const parsed = parseNodeCompleteEvent(data)
          if (parsed) {
            updateNodeExecution(parsed.node_id, {
              status: parsed.status,
              completedAt: Date.now(),
            })
          }
          break
        }
        case 'block': {
          const parsed = parseBlockEvent(data)
          if (parsed?.block) addBlock(parsed.block)
          break
        }
        case 'error': {
          const parsed = parseErrorEvent(data)
          if (parsed) setStoreError(`${parsed.code}: ${parsed.message}`)
          break
        }
        case 'final': {
          const parsed = parseFinalEvent(data)
          if (parsed) {
            setStoreStatus(parsed.status)
            if (parsed.trace_id) setTraceId(parsed.trace_id)
            const elapsed = Date.now() - streamStartRef.current
            if (elapsed > 0) setDuration(elapsed)
            if (parsed.error) setStoreError(parsed.error)
          }
          setIsStreaming(false)
          break
        }
        default:
          break
      }
    },
    [setTitle, setSummary, addBlock, setStoreError, setStoreStatus, setTraceId, setDuration, addNodeExecution, updateNodeExecution]
  )

  /**
   * Parse SSE buffer into events
   * SSE format: event: type\ndata: {...}\n\n
   */
  const parseSSEBuffer = useCallback(
    (buffer: string): { parsed: Array<{ type: string; data: string }>; remaining: string } => {
      const events: Array<{ type: string; data: string }> = []
      const parts = buffer.split('\n\n')
      const remaining = parts.pop() || ''

      for (const part of parts) {
        if (!part.trim()) continue

        const lines = part.split('\n')
        let eventType = 'message'
        let data = ''

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7)
          } else if (line.startsWith('data: ')) {
            data += line.slice(6)
          }
        }

        if (data) {
          events.push({ type: eventType, data })
        }
      }

      return { parsed: events, remaining }
    },
    []
  )

  /**
   * Start streaming via POST request with fetch + ReadableStream
   * This bypasses EventSource GET URL length limits
   */
  const startStream = useCallback(
    async (query: string, context?: Record<string, unknown>) => {
      // Clear previous state (including any orchestrator workflow progress from a prior query)
      clearBlocks()
      resetOrchestratorState()
      setStoreError(null)
      setStoreStatus('streaming')
      setStatus('connecting')
      setIsStreaming(true)
      setError(null)

      // Fail fast if no token — better UX than a network 401
      const token = useAuth.getState().token
      if (!token) {
        setStoreError('Authentication token missing. Please log in again.')
        setIsStreaming(false)
        setStatus('error')
        return
      }

      // Create abort controller for cleanup
      abortControllerRef.current = new AbortController()
      streamStartRef.current = Date.now()

      try {
        const requestBody = { query, context: context || {} }

        const response = await fetchWithAuth(`${sseBaseUrl}/agents/${agentId}/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
          },
          body: JSON.stringify(requestBody),
          signal: abortControllerRef.current.signal,
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        setStatus('connected')
        setStoreStatus('streaming')

        const reader = response.body!.getReader()
        const decoder = new TextDecoder()
        let buffer = ''

        // Read stream until complete
        while (true) {
          const { done, value } = await reader.read()
          if (done) break

          buffer += decoder.decode(value, { stream: true })

          // Parse complete SSE events from buffer
          const { parsed, remaining } = parseSSEBuffer(buffer)
          buffer = remaining

          // Process each parsed event
          for (const event of parsed) {
            try {
              const data = JSON.parse(event.data)
              handleSSEEvent(event.type, data)
            } catch {
              // Skip malformed SSE data
            }
          }
        }

        // Stream completed successfully
        setStatus('disconnected')
        setIsStreaming(false)
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          setStatus('disconnected')
        } else if (err instanceof AuthExpiredError) {
          // Session expired — logout already triggered by fetchWithAuth
          setStatus('disconnected')
        } else {
          const error = err instanceof Error ? err : new Error(String(err))
          setError(error)
          setStoreError(error.message)
          setStatus('error')
        }
        setIsStreaming(false)
      }
    },
    [
      agentId,
      sseBaseUrl,
      clearBlocks,
      resetOrchestratorState,
      setStoreError,
      setStoreStatus,
      handleSSEEvent,
      parseSSEBuffer,
    ]
  )

  /**
   * Stop the current stream
   */
  const stopStream = useCallback(() => {
    abortControllerRef.current?.abort()
    setIsStreaming(false)
    setStatus('disconnected')
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort()
    }
  }, [])

  return {
    status,
    startStream,
    stopStream,
    error,
    isStreaming,
  }
}
